import { defineComponent as _defineComponent } from 'vue'
import { ref, onMounted, inject, onBeforeUnmount, onActivated } from "vue";
import Page from "@/components/shared/Page.vue";
import NavigationBar from "@/components/shared/NavigationBar.vue";
import {
  PROVIDE_KEY_REMOVE_LOADING_IMAGE,
  PROVIDE_KEY_SHOW_ERROR_MODAL,
  PROVIDE_KEY_SHOW_LOADING_IMAGE,
  RemoveLoadingImage,
  RouterName,
  ShowErrorModal,
  ShowLoadingImage,
} from "@/types";
import NewRouteApplicationRow from "@/components/bbs/newRoute/NewRouteApplicationRow.vue";
import SearchInput from "@/components/shared/inputs/SearchInput.vue";
import router from "@/router";
import InlineSvg from "vue-inline-svg";
import NewRouteApplicationGuide from "@/components/bbs/newRoute/NewRouteApplicationGuide.vue";
import { useBbsStore } from "@/stores/bbsStore";
import { INewRouteApplication } from "@/domain/entities/bbs";
import CheckboxInput from "@/components/shared/inputs/CheckboxInput.vue";
import Modal from "@/components/shared/modal/base/Modal.vue";
import { useServiceStore } from "@/stores/serviceStore";
import ScrollTopControl from "@/components/shared/ScrollTopControl.vue";
import PageModal from "@/components/shared/modal/base/PageModal.vue";


const __default__ = {
  name: "NewRouteApplicationList",
};

export default /*#__PURE__*/_defineComponent({
  ...__default__,
  setup(__props, { expose: __expose }) {
  __expose();

const showErrorModal = inject(PROVIDE_KEY_SHOW_ERROR_MODAL) as ShowErrorModal;
const showLoadingImage = inject(
  PROVIDE_KEY_SHOW_LOADING_IMAGE
) as ShowLoadingImage;
const removeLoadingImage = inject(
  PROVIDE_KEY_REMOVE_LOADING_IMAGE
) as RemoveLoadingImage;

const serviceStore = useServiceStore();
const bbsStore = useBbsStore();

const currentPage = ref(0);
const currentLimit = 20;
const totalCount = ref(0);

const applications = ref<INewRouteApplication[]>([]);
const isLoading = ref(false);
const listContainer = ref();
const searchKeyword = ref();

const isMyPostVisible = ref(false);

const toggleIsMyPostVisible = (checked: boolean) => {
  isMyPostVisible.value = checked;
  searchApplication();
};

const clearSearchKeyword = () => {
  searchKeyword.value = "";
  searchApplication();
};

const searchApplication = async () => {
  currentPage.value = 0;
  applications.value = [];
  await getApplicationList();
};

// 하루 작성 횟수 초과 모달 처리
const maxDailyTalkCreationCount =
  serviceStore.serviceInfo?.maxDailyTalkCreationCount;

const shouldShowDailyWritingCountLimitModal = ref(false);
const toggleShowDailyWritingCountLimitModal = (show: boolean) => {
  shouldShowDailyWritingCountLimitModal.value = show;
};

const wasClickApplyBtn = ref(false);

const onClickApplyBtn = async () => {
  showLoadingImage();
  const listResult = await bbsStore.getNewRouteApplications(
    "",
    0,
    undefined,
    true,
    true
  );
  removeLoadingImage();

  const totalCount = listResult.data?.totalCount ?? 0;
  const isExceeded = maxDailyTalkCreationCount
    ? totalCount >= maxDailyTalkCreationCount
    : false;

  if (isExceeded) {
    toggleShowDailyWritingCountLimitModal(true);
  } else {
    wasClickApplyBtn.value = true;
    toggleShowApplicationGuide(true);
  }
};

const shouldShowApplicationGuide = ref(false);
const toggleShowApplicationGuide = (show: boolean) => {
  shouldShowApplicationGuide.value = show;
};

const onClosedNewRouteApplicationGuide = () => {
  toggleShowApplicationGuide(false);

  if (wasClickApplyBtn.value) {
    goToApplication();
    wasClickApplyBtn.value = false;
  }
};

const goToApplication = () => {
  router.push({ name: RouterName.NewRouteApplication });
};

const goToApplicationDetail = (selectedApplication: INewRouteApplication) => {
  router.push({
    name: RouterName.NewRouteApplicationDetail,
    params: { uid: selectedApplication.uid },
  });
};

const getApplicationList = async () => {
  // 이미 로딩 중이면 중복 호출 방지
  if (isLoading.value) return;

  isLoading.value = true;
  const currentOffset = currentPage.value * currentLimit;
  const listResult = await bbsStore.getNewRouteApplications(
    searchKeyword.value,
    currentOffset,
    currentLimit,
    isMyPostVisible.value
  );
  isLoading.value = false;

  if (!listResult.isSuccess) {
    showErrorModal(
      "신규 노선/정류장 신청 목록 오류",
      listResult.errorMessage,
      listResult.resultCode,
      listResult.errorTime
    );
    return;
  }

  if (!listResult.data) return;
  totalCount.value = listResult.data.totalCount;
  if (listResult.data.list.length === 0) return;
  applications.value = [...applications.value, ...listResult.data.list];
  currentPage.value += 1;
};

const handleScroll = () => {
  if (!listContainer.value) return;

  const nearBottom =
    listContainer.value.clientHeight + listContainer.value.scrollTop >
    listContainer.value.scrollHeight - 200;

  if (!nearBottom) return;
  if (applications.value.length >= totalCount.value) return;
  getApplicationList();
};

onMounted(() => {
  if (!listContainer.value) return;
  listContainer.value.addEventListener("scroll", handleScroll);
});

onActivated(async () => {
  showLoadingImage();
  await searchApplication();
  removeLoadingImage();
});

onBeforeUnmount(() => {
  if (!listContainer.value) return;
  listContainer.value.removeEventListener("scroll", handleScroll);
});

const __returned__ = { showErrorModal, showLoadingImage, removeLoadingImage, serviceStore, bbsStore, currentPage, currentLimit, totalCount, applications, isLoading, listContainer, searchKeyword, isMyPostVisible, toggleIsMyPostVisible, clearSearchKeyword, searchApplication, maxDailyTalkCreationCount, shouldShowDailyWritingCountLimitModal, toggleShowDailyWritingCountLimitModal, wasClickApplyBtn, onClickApplyBtn, shouldShowApplicationGuide, toggleShowApplicationGuide, onClosedNewRouteApplicationGuide, goToApplication, goToApplicationDetail, getApplicationList, handleScroll, Page, NavigationBar, NewRouteApplicationRow, SearchInput, get InlineSvg() { return InlineSvg }, NewRouteApplicationGuide, CheckboxInput, Modal, ScrollTopControl, PageModal }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})